import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import pandaAnimation from '../assets/animations/panda call center.json';
import './PandaCompanion.css';

const PandaCompanion = ({ isOpen, onClose, onDataCollected }) => {
  const [userInput, setUserInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // IMPORTANT: Replace with your actual Gemini API key
  // Make sure to keep this secure - in production, use environment variables
  const GEMINI_API_KEY = 'AIzaSyBx2UReN2SQOFFcDKG2Ngy-4elMNb7GnXs';
  const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`;

  const systemPrompt = `You are Zen, a friendly and caring panda mental health therapist. Your goal is to:

1. Be warm, empathetic, and supportive
2. Ask thoughtful follow-up questions based on user responses
3. Use active listening techniques
4. Provide gentle guidance and coping strategies
5. Keep responses concise (2-3 sentences max)
6. Use emojis occasionally to show warmth
7. Ask one meaningful question at a time
8. Focus on understanding the user's emotional state
9. Offer practical mental wellness tips when appropriate
10. Always maintain a caring, non-judgmental tone

Remember: You're a supportive companion, not a replacement for professional therapy. If someone mentions serious mental health concerns, gently suggest they speak with a mental health professional.`;

  const extractTopicFromMessage = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('stress') || lowerMessage.includes('anxiety')) return 'Stress & Anxiety';
    if (lowerMessage.includes('sad') || lowerMessage.includes('depressed')) return 'Sadness & Depression';
    if (lowerMessage.includes('happy') || lowerMessage.includes('joy')) return 'Happiness & Joy';
    if (lowerMessage.includes('work') || lowerMessage.includes('job')) return 'Work & Career';
    if (lowerMessage.includes('relationship') || lowerMessage.includes('family')) return 'Relationships';
    if (lowerMessage.includes('sleep') || lowerMessage.includes('tired')) return 'Sleep & Rest';
    if (lowerMessage.includes('goal') || lowerMessage.includes('dream')) return 'Goals & Dreams';
    return 'General Wellness';
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen && chatHistory.length === 0) {
      addMessage("Hi there! 🐼 I'm Zen, your friendly wellness companion. I'm here to listen and support you on your mental health journey. What would you like me to call you?", 'bot');
    }
  }, [isOpen]);

  const addMessage = (message, sender, delay = 0) => {
    setIsTyping(sender === 'bot');
    setTimeout(() => {
      setChatHistory(prev => [...prev, { message, sender, timestamp: Date.now() }]);
      setIsTyping(false);
    }, delay);
  };

  const callGeminiAPI = async (userMessage, conversationHistory) => {
    try {
      setIsLoading(true);
      
      // Build conversation context
      let contextMessages = [
        {
          role: "user",
          parts: [{ text: systemPrompt }]
        }
      ];
      
      // Add conversation history
      conversationHistory.forEach(msg => {
        contextMessages.push({
          role: msg.sender === 'user' ? 'user' : 'model',
          parts: [{ text: msg.message }]
        });
      });
      
      // Add current user message
      contextMessages.push({
        role: 'user',
        parts: [{ text: userMessage }]
      });

      console.log('Sending request to Gemini API...');
      const response = await fetch(GEMINI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: contextMessages,
          generationConfig: {
            temperature: 0.9,
            topK: 1,
            topP: 1,
            maxOutputTokens: 2048,
            stopSequences: []
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            }
          ]
        })
      });

      console.log('API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response Data:', data);
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text.trim();
      } else if (data.error) {
        console.error('API Error:', data.error);
        throw new Error(data.error.message || 'Error from Gemini API');
      } else {
        console.error('Unexpected API response format:', data);
        throw new Error('Unexpected response format from Gemini API');
      }
    } catch (error) {
      console.error('Error in callGeminiAPI:', error);
      return `I'm having some trouble connecting to my knowledge base right now. Here's what happened: ${error.message}. Could you try again in a moment? 🐼`;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserInput = async () => {
    if (!userInput.trim()) return;

    const currentInput = userInput.trim();
    setUserInput('');
    
    // Add user message
    addMessage(currentInput, 'user');
    
    // Check if this is the first message (asking for name)
    if (!conversationStarted && chatHistory.length <= 1) {
      setConversationStarted(true);
      
      setTimeout(() => {
        addMessage(`Nice to meet you, ${currentInput}! 😊 I'm really glad you're here. How are you feeling today? Take your time - there's no pressure to share more than you're comfortable with.`, 'bot');
      }, 1000);
      return;
    }

    // Get AI response for ongoing conversation
    setTimeout(async () => {
      const aiResponse = await callGeminiAPI(currentInput, chatHistory);
      addMessage(aiResponse, 'bot');
      
      // Save conversation for Journey analysis
      const conversationEntry = {
        date: new Date().toISOString(),
        userMessage: currentInput,
        aiResponse: aiResponse,
        topic: extractTopicFromMessage(currentInput)
      };
      
      const existingConversations = JSON.parse(localStorage.getItem('pandaConversationHistory') || '[]');
      const updatedConversations = [conversationEntry, ...existingConversations].slice(0, 50); // Keep last 50 conversations
      localStorage.setItem('pandaConversationHistory', JSON.stringify(updatedConversations));
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleUserInput();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="panda-companion-overlay">
      <div className="panda-companion-modal">
        <div className="panda-header">
          <div className="panda-animation">
            <Lottie 
              animationData={pandaAnimation} 
              style={{ width: 80, height: 80 }}
              loop={true}
            />
          </div>
          <div className="panda-title">
            <h3>Zen - Your Wellness Companion</h3>
            <span className="panda-status">{isLoading ? 'Thinking...' : 'Online'}</span>
          </div>
          <button className="panda-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="panda-chat-container">
          {chatHistory.map((chat, index) => (
            <div key={index} className={`panda-message ${chat.sender}`}>
              <div className="message-content">
                {chat.message}
              </div>
            </div>
          ))}
          
          {(isTyping || isLoading) && (
            <div className="panda-message bot">
              <div className="message-content typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="panda-input-area">
          <div className="panda-input-container">
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={conversationStarted ? "Share what's on your mind..." : "Tell me your name..."}
              className="panda-input"
              disabled={isLoading}
              autoFocus
            />
            <button 
              onClick={handleUserInput}
              className="panda-send-btn"
              disabled={!userInput.trim() || isLoading}
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PandaCompanion;